import { useEffect, useState } from 'react';
import axios from 'axios';
import wsManager from '../../../services/wsManager';
import '../../../css/blurred-preview.css';

// This module (course-preview + the topic/exercise builders it mirrors)
// hardcodes Ukrainian directly throughout rather than going through
// variables/language.jsx - matching that existing convention here rather
// than being the one i18n-aware component in the whole subsystem.

const LIST_ENDPOINT = '/api/teacher-approval-requests';
const STATUS_ENDPOINT = '/api/teacher-approval-requests/status';

function matchesElement(req, elementId) {
   const target = req.topicElement || req.exerciseTemplateElement || req.exerciseTemplateInteractiveElement;
   return target && String(target.id) === String(elementId);
}

// Shared by TopicPreviewPanel.jsx's BlockPreviewEntry, ExercisePreviewBlock.jsx
// AND WorkspaceTopicSection.jsx (the per-student activity view) - both already
// wrap their rendered block in the same .course-preview-block-content shape.
//
// Two modes, switched on whether `studentId` is passed:
// - No studentId (course-builder Preview tab): a blurEnabled block always
//   renders blurred - this view isn't scoped to any one student, so there's
//   no single "resolved for me" state to check. What's live is the list of
//   every student currently waiting on it, each with its own Approve/Reject.
// - studentId given (StudentWorkspaceView's per-student activity review):
//   this student's own resolution state is checked and, once APPROVED,
//   the block un-blurs here permanently - this view genuinely is scoped to
//   one student, so "wait forever" would be wrong. PENDING still shows the
//   same blur+prompt (reusing GET ?student= to get the actual request
//   object for Approve/Reject, filtered client-side to this element - that
//   endpoint already returns PENDING-only), NONE/REJECTED just stays
//   blurred with no prompt.
export default function BlurredPreviewWrapper({ elementType, elementId, blurEnabled, studentId, children }) {
   const [pending, setPending] = useState([]);
   const [resolvedStatus, setResolvedStatus] = useState(null);

   const load = () => {
      if (studentId) {
         axios.get(LIST_ENDPOINT, { params: { student: studentId }, withCredentials: true })
            .then(r => {
               const mine = (Array.isArray(r.data) ? r.data : []).filter(req => matchesElement(req, elementId));
               setPending(mine);
               if (mine.length > 0) {
                  setResolvedStatus('PENDING');
                  return;
               }
               axios.get(STATUS_ENDPOINT, { params: { student: studentId, elementType, elementId }, withCredentials: true })
                  .then(r2 => setResolvedStatus(r2.data.status))
                  .catch(() => setResolvedStatus('NONE'));
            })
            .catch(() => { setPending([]); setResolvedStatus('NONE'); });
      } else {
         axios.get(LIST_ENDPOINT, { params: { elementType, elementId }, withCredentials: true })
            .then(r => setPending(Array.isArray(r.data) ? r.data : []))
            .catch(() => setPending([]));
      }
   }

   useEffect(() => {
      if (!blurEnabled) return;
      load();
      const topic = '/topic/element/' + elementType + '/' + elementId + '/reveal';
      // any status change on this element (new request, approved, rejected
      // elsewhere) - just refetch rather than trying to patch the list from
      // the event payload alone, same "refetch on any ws event" idiom
      // TopicCanvasView.jsx already uses for its own element sync.
      const handler = () => load();
      wsManager.subscribe(topic, handler);
      return () => wsManager.unsubscribe(topic, handler);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [elementType, elementId, blurEnabled, studentId]);

   const handleApprove = (id) => {
      axios.post(LIST_ENDPOINT + '/' + id + '/approve', {}, { withCredentials: true })
         .then(load).catch(err => console.log(err));
   }
   const handleReject = (id) => {
      axios.post(LIST_ENDPOINT + '/' + id + '/reject', {}, { withCredentials: true })
         .then(load).catch(err => console.log(err));
   }

   if (!blurEnabled) return children;
   if (studentId && resolvedStatus === 'APPROVED') return children;

   return (
      <div className='blurred-preview'>
         <div className='blurred-preview-content'>{children}</div>
         <div className='blurred-preview-badge'>Розмито до підтвердження</div>
         {pending.length > 0 ? (
            <div className='blurred-preview-requests'>
               {pending.map(req => (
                  <div className='blurred-preview-request-row' key={req.id}>
                     <span className='blurred-preview-request-text'>{req.message}</span>
                     <div className='blurred-preview-request-actions'>
                        <button type='button' className='blurred-preview-approve-btn' onClick={() => handleApprove(req.id)}>Показати</button>
                        <button type='button' className='blurred-preview-reject-btn' onClick={() => handleReject(req.id)}>Відхилити</button>
                     </div>
                  </div>
               ))}
            </div>
         ) : ''}
      </div>
   );
}
